<template>
  <el-dialog
    :visible.sync="visibleProxy"
    :show-close="false"
    width="92%"
    append-to-body
    :close-on-click-modal="false"
    custom-class="target-dialog"
  >
    <div class="dialog-inner">
      <div class="dialog-header">
        <div class="dialog-title-wrap">
          <div class="dialog-title">
            {{ isViewMode ? '查看目标' : '设定目标' }}
            <span v-if="currentStoreName" class="store-tag">{{ currentStoreName }}</span>
          </div>
          <div class="dialog-subtitle">门店开单业绩目标 = 事业部目标；{{ $catName('生美') }} + {{ $catName('科美') }} + {{ $catName('医美') }}须与其一致</div>
        </div>
        <span class="dialog-close" @click="handleClose"><i class="el-icon-close"></i></span>
      </div>

      <div class="toolbar-row">
        <el-date-picker
          v-model="viewMonth"
          type="month"
          placeholder="选择月份"
          format="yyyy年MM月"
          value-format="yyyyMM"
          size="small"
          :clearable="false"
          prefix-icon="el-icon-date"
          @change="onMonthChange"
          class="month-picker"
        />
        <span class="mode-badge" :class="isViewMode ? 'mode-badge--readonly' : 'mode-badge--edit'">
          {{ isViewMode ? '只读' : '可编辑' }}
        </span>
      </div>

      <div class="summary-card">
        <div class="summary-head">
          <div class="summary-title">
            <i class="el-icon-data-analysis"></i>
            {{ monthLabel }} 门店开单业绩目标
          </div>
          <div class="summary-amount">¥ {{ formatMoney(computedStoreTarget) }}</div>
        </div>
        <div class="summary-hint">
          {{ $catName('生美') }} + {{ $catName('科美') }} + {{ $catName('医美') }}：¥ {{ formatMoney(categoryTargetSum) }}
          <span v-if="targetMismatch" class="summary-warn">（与事业部目标不一致）</span>
        </div>
      </div>

      <div class="dialog-body" v-loading="loading">
        <el-form :model="form" label-position="right" label-width="108px" size="small" class="target-form">
          <div class="section-panel">
            <div class="section-head">
              <i class="el-icon-office-building"></i>
              <span>部门归属与业绩目标</span>
            </div>
            <div
              v-for="row in deptRows"
              :key="row.key"
              class="dept-row"
            >
              <div class="dept-row-label">
                {{ row.label }}<span v-if="row.tag" class="dept-tag">（{{ row.tag | catName }}）</span>
              </div>
              <div class="dept-row-fields">
                <el-form-item label="归属部门" required class="dept-form-item">
                  <el-select
                    v-model="form[row.key]"
                    :placeholder="`请选择${row.label}`"
                    clearable
                    filterable
                    :disabled="isViewMode"
                    popper-append-to-body
                    class="full-width"
                  >
                    <el-option
                      v-for="item in getDeptOptions(row.optionsKey)"
                      :key="item.id"
                      :label="item.fullName"
                      :value="item.id"
                    />
                  </el-select>
                </el-form-item>
                <el-form-item label="业绩目标" required class="dept-form-item dept-form-item--target">
                  <el-input-number
                    v-model="form[row.targetKey]"
                    :min="0"
                    :step="1000"
                    :precision="2"
                    controls-position="right"
                    :controls="false"
                    placeholder="请输入"
                    :disabled="isViewMode"
                    class="full-width"
                  />
                </el-form-item>
              </div>
            </div>
          </div>

          <div class="section-panel section-panel--mt">
            <div class="section-head">
              <i class="el-icon-s-data"></i>
              <span>其他指标</span>
            </div>
            <div class="extra-grid">
              <el-form-item label="消耗目标" required>
                <el-input-number
                  v-model="form.consumeTarget"
                  :min="0"
                  :step="100"
                  :precision="2"
                  controls-position="right"
                  :controls="false"
                  placeholder="请输入"
                  :disabled="isViewMode"
                  class="full-width"
                />
              </el-form-item>
              <el-form-item label="门店生命线" required>
                <el-input-number
                  v-model="form.lifeline"
                  :min="0"
                  :step="100"
                  :precision="2"
                  controls-position="right"
                  :controls="false"
                  placeholder="请输入"
                  :disabled="isViewMode"
                  class="full-width"
                />
              </el-form-item>
              <el-form-item label="人员目标" required>
                <el-input
                  v-model.number="form.headcountTarget"
                  type="number"
                  placeholder="请输入"
                  min="0"
                  step="1"
                  :disabled="isViewMode"
                />
              </el-form-item>
              <el-form-item label="店助阶段一" required>
                <el-input
                  v-model.number="form.assistPhase1"
                  type="number"
                  placeholder="请输入"
                  min="0"
                  step="1"
                  :disabled="isViewMode"
                />
              </el-form-item>
              <el-form-item label="店助阶段二" required class="extra-span-2">
                <el-input
                  v-model.number="form.assistPhase2"
                  type="number"
                  placeholder="请输入"
                  min="0"
                  step="1"
                  :disabled="isViewMode"
                />
              </el-form-item>
            </div>
          </div>
        </el-form>
      </div>

      <div class="dialog-footer">
        <el-button
          v-if="!isViewMode"
          type="primary"
          size="small"
          class="pill-btn"
          :loading="saving"
          @click="handleSave"
        >
          保存目标
        </el-button>
        <el-button size="small" class="pill-btn pill-btn--plain" @click="handleClose">
          {{ isViewMode ? '关闭' : '取消' }}
        </el-button>
        <el-button
          v-if="!isViewMode"
          size="small"
          class="pill-btn pill-btn--ghost"
          @click="handleCopyLastMonth"
        >
          复制上月
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import {
  getLqMdTargetList,
  getLqMdTargetInfo,
  createLqMdTarget,
  updateLqMdTarget,
  getStoreMdxxInfo,
  getDepartmentOptions,
  getOrganizeInfo
} from '@/api/lqMdTarget'

const DEPT_ROOT_IDS = {
  businessUnit: '734724946332222725',
  techDepartment: '734725044101448965',
  educationDepartment: '734725010941281541',
  majorProjectDepartment: '734725079920805125'
}

const DEPT_ROWS = [
  { key: 'businessUnit', targetKey: 'businessUnitTarget', label: '事业部', tag: '', optionsKey: 'businessUnitOptions' },
  { key: 'techDepartment', targetKey: 'techDepartmentTarget', label: '科技部', tag: '科美', optionsKey: 'techDepartmentOptions' },
  { key: 'educationDepartment', targetKey: 'educationDepartmentTarget', label: '教育部', tag: '生美', optionsKey: 'educationDepartmentOptions' },
  { key: 'majorProjectDepartment', targetKey: 'majorProjectDepartmentTarget', label: '大项目部', tag: '医美', optionsKey: 'majorProjectDepartmentOptions' }
]

export default {
  name: 'StoreTargetDialog',
  props: {
    visible: { type: Boolean, default: false },
    currentStoreId: { type: String, default: '' },
    currentStoreName: { type: String, default: '' },
    monthValue: { type: String, default: '' }
  },
  data() {
    return {
      savedTarget: null,
      viewMonth: '',
      saving: false,
      loading: false,
      businessUnitOptions: [],
      techDepartmentOptions: [],
      educationDepartmentOptions: [],
      majorProjectDepartmentOptions: [],
      form: this.getEmptyForm()
    }
  },
  computed: {
    deptRows() {
      return DEPT_ROWS
    },
    visibleProxy: {
      get() { return this.visible },
      set(val) { this.$emit('update:visible', val) }
    },
    isViewMode() {
      return this.savedTarget && this.savedTarget.configured
    },
    monthLabel() {
      const m = this.viewMonth || this.monthValue
      if (m && m.length === 6) {
        return `${m.slice(0, 4)}年${m.slice(4, 6)}月`
      }
      return ''
    },
    lastMonth() {
      const m = this.viewMonth || this.monthValue
      if (m && m.length === 6) {
        const year = parseInt(m.slice(0, 4))
        const month = parseInt(m.slice(4, 6))
        const lastDate = new Date(year, month - 2, 1)
        return `${lastDate.getFullYear()}${String(lastDate.getMonth() + 1).padStart(2, '0')}`
      }
      return ''
    },
    computedStoreTarget() {
      const businessUnit = Number(this.form.businessUnitTarget) || 0
      const categorySum = this.categoryTargetSum
      return businessUnit > 0 ? businessUnit : categorySum
    },
    categoryTargetSum() {
      return (Number(this.form.educationDepartmentTarget) || 0)
        + (Number(this.form.techDepartmentTarget) || 0)
        + (Number(this.form.majorProjectDepartmentTarget) || 0)
    },
    targetMismatch() {
      const businessUnit = Number(this.form.businessUnitTarget) || 0
      const categorySum = this.categoryTargetSum
      if (businessUnit <= 0 && categorySum <= 0) return false
      return Math.abs(businessUnit - categorySum) > 0.009
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.viewMonth = this.monthValue || this.getCurrentMonth()
        this.loadMonthData()
      }
    }
  },
  methods: {
    getEmptyForm() {
      return {
        businessUnit: '',
        techDepartment: '',
        educationDepartment: '',
        majorProjectDepartment: '',
        businessUnitTarget: null,
        techDepartmentTarget: null,
        educationDepartmentTarget: null,
        majorProjectDepartmentTarget: null,
        consumeTarget: null,
        headcountTarget: null,
        lifeline: null,
        assistPhase1: null,
        assistPhase2: null
      }
    },
    getCurrentMonth() {
      const now = new Date()
      return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`
    },
    formatMoney(value) {
      const n = Number(value) || 0
      return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    normalizeDeptId(id) {
      if (id == null || id === '') return ''
      return String(id)
    },
    extractOptionList(res) {
      const data = res && res.data
      if (!data) return []
      if (Array.isArray(data.list)) return data.list
      if (Array.isArray(data)) return data
      return []
    },
    normalizeDeptOptions(list) {
      return (list || []).map(item => ({
        id: this.normalizeDeptId(item.id || item.Id),
        fullName: item.fullName || item.FullName || '无'
      })).filter(item => item.id)
    },
    getDeptOptions(optionsKey) {
      return this[optionsKey] || []
    },
    isTargetConfigured(target) {
      if (!target) return false
      const categorySum = Number(target.educationDepartmentTarget || 0)
        + Number(target.techDepartmentTarget || 0)
        + Number(target.majorProjectDepartmentTarget || 0)
      const businessUnit = Number(target.businessUnitTarget || 0)
      if (Number(target.storeTarget || 0) > 0 || businessUnit > 0 || categorySum > 0) return true
      if (Number(target.storeConsumeTarget || 0) > 0) return true
      if (Number(target.storeHeadcountTarget || 0) > 0) return true
      if (Number(target.storeLifeline || 0) > 0) return true
      return false
    },
    resolveDeptId(value, options) {
      if (value == null || value === '') return ''
      const str = String(value)
      if (/^\d{15,}$/.test(str)) return str
      const list = options || []
      const byId = list.find(item => item.id === str || item.Id === str)
      if (byId) return byId.id || byId.Id
      const byName = list.find(item => item.fullName === str || item.FullName === str)
      return byName ? (byName.id || byName.Id) : str
    },
    mapTargetToForm(target) {
      return {
        businessUnit: this.resolveDeptId(target.businessUnit || target.BusinessUnit, this.businessUnitOptions),
        techDepartment: this.resolveDeptId(target.techDepartment || target.TechDepartment, this.techDepartmentOptions),
        educationDepartment: this.resolveDeptId(target.educationDepartment || target.EducationDepartment, this.educationDepartmentOptions),
        majorProjectDepartment: this.resolveDeptId(target.majorProjectDepartment || target.MajorProjectDepartment, this.majorProjectDepartmentOptions),
        businessUnitTarget: target.businessUnitTarget != null ? target.businessUnitTarget : null,
        techDepartmentTarget: target.techDepartmentTarget != null ? target.techDepartmentTarget : null,
        educationDepartmentTarget: target.educationDepartmentTarget != null ? target.educationDepartmentTarget : null,
        majorProjectDepartmentTarget: target.majorProjectDepartmentTarget != null ? target.majorProjectDepartmentTarget : null,
        consumeTarget: target.storeConsumeTarget != null ? target.storeConsumeTarget : null,
        headcountTarget: target.storeHeadcountTarget != null ? target.storeHeadcountTarget : null,
        lifeline: target.storeLifeline != null ? target.storeLifeline : null,
        assistPhase1: target.assistantHeadcountTargetStage1 != null ? target.assistantHeadcountTargetStage1 : null,
        assistPhase2: target.assistantHeadcountTargetStage2 != null ? target.assistantHeadcountTargetStage2 : null
      }
    },
    buildPayload() {
      return {
        storeId: this.currentStoreId,
        month: this.viewMonth,
        businessUnit: this.form.businessUnit || '',
        techDepartment: this.form.techDepartment || '',
        educationDepartment: this.form.educationDepartment || '',
        majorProjectDepartment: this.form.majorProjectDepartment || '',
        businessUnitTarget: Number(this.form.businessUnitTarget) || 0,
        techDepartmentTarget: Number(this.form.techDepartmentTarget) || 0,
        educationDepartmentTarget: Number(this.form.educationDepartmentTarget) || 0,
        majorProjectDepartmentTarget: Number(this.form.majorProjectDepartmentTarget) || 0,
        storeTarget: Number(this.form.businessUnitTarget) || this.categoryTargetSum,
        storeConsumeTarget: Number(this.form.consumeTarget) || 0,
        storeHeadcountTarget: Number(this.form.headcountTarget) || 0,
        storeLifeline: Number(this.form.lifeline) || 0,
        assistantHeadcountTargetStage1: Number(this.form.assistPhase1) || 0,
        assistantHeadcountTargetStage2: Number(this.form.assistPhase2) || 0,
        storeProjectTarget: 0
      }
    },
    async loadDepartmentOptions() {
      const load = async (key, listKey) => {
        const parentId = DEPT_ROOT_IDS[key]
        try {
          const res = await getDepartmentOptions(parentId)
          this[listKey] = this.normalizeDeptOptions(this.extractOptionList(res))
        } catch (e) {
          console.error(`加载${key}部门选项失败`, e)
          this[listKey] = []
        }
      }
      await Promise.all([
        load('businessUnit', 'businessUnitOptions'),
        load('techDepartment', 'techDepartmentOptions'),
        load('educationDepartment', 'educationDepartmentOptions'),
        load('majorProjectDepartment', 'majorProjectDepartmentOptions')
      ])
    },
    async ensureMissingDeptOptions() {
      const tasks = DEPT_ROWS.map(async (row) => {
        const deptId = this.form[row.key]
        if (!deptId) return
        const options = this[row.optionsKey]
        if (options.some(o => o.id === deptId)) return
        try {
          const res = await getOrganizeInfo(deptId)
          const info = res.data || {}
          const name = info.fullName || info.FullName || ''
          if (name) {
            options.push({ id: deptId, fullName: name })
          }
        } catch (e) {
          // 回显失败不影响编辑
        }
      })
      await Promise.all(tasks)
    },
    async loadStoreDepartmentDefaults() {
      if (!this.currentStoreId) return
      try {
        const res = await getStoreMdxxInfo(this.currentStoreId)
        const store = res.data || {}
        if (!this.form.businessUnit && store.syb) {
          this.form.businessUnit = this.resolveDeptId(store.syb, this.businessUnitOptions)
        }
        if (!this.form.techDepartment && store.kjb) {
          this.form.techDepartment = this.resolveDeptId(store.kjb, this.techDepartmentOptions)
        }
        if (!this.form.educationDepartment && store.jyb) {
          this.form.educationDepartment = this.resolveDeptId(store.jyb, this.educationDepartmentOptions)
        }
        if (!this.form.majorProjectDepartment && store.dxmb) {
          this.form.majorProjectDepartment = this.resolveDeptId(store.dxmb, this.majorProjectDepartmentOptions)
        }
      } catch (e) {
        // 预填失败不影响设定
      }
    },
    onMonthChange() {
      this.loadMonthData()
    },
    async loadMonthData() {
      if (!this.currentStoreId || !this.viewMonth) return

      this.loading = true
      this.savedTarget = null
      this.form = this.getEmptyForm()

      try {
        await this.loadDepartmentOptions()

        const res = await getLqMdTargetList({
          storeId: this.currentStoreId,
          month: this.viewMonth,
          currentPage: 1,
          pageSize: 1
        })

        const list = res.data?.list || []
        if (list.length > 0) {
          let target = list[0]
          try {
            const detailRes = await getLqMdTargetInfo(target.id)
            if (detailRes && detailRes.data) {
              target = detailRes.data
            }
          } catch (e) {
            // 详情失败时回退列表行
          }
          const configured = this.isTargetConfigured(target)
          this.savedTarget = { id: target.id || target.Id, configured }
          this.form = this.mapTargetToForm(target)
        } else {
          await this.loadStoreDepartmentDefaults()
        }
        await this.ensureMissingDeptOptions()
      } catch (e) {
        console.error('加载门店目标失败', e)
      } finally {
        this.loading = false
      }
    },
    handleClose() {
      this.visibleProxy = false
    },
    validateForm() {
      for (const row of DEPT_ROWS) {
        if (!this.form[row.key]) {
          this.$message.warning(`请选择${row.label}归属部门`)
          return false
        }
        if (this.form[row.targetKey] == null || this.form[row.targetKey] === '') {
          this.$message.warning(`请填写${row.label}业绩目标`)
          return false
        }
      }
      if (this.computedStoreTarget <= 0) {
        this.$message.warning('请填写事业部业绩目标（门店开单业绩目标）')
        return false
      }
      if (this.categoryTargetSum <= 0) {
        this.$message.warning(`请填写${this.$catName('生美')}、${this.$catName('科美')}、${this.$catName('医美')}业绩目标`)
        return false
      }
      if (this.targetMismatch) {
        this.$message.warning(`${this.$catName('生美')} + ${this.$catName('科美')} + ${this.$catName('医美')}合计须等于事业部业绩目标`)
        return false
      }
      if (this.form.consumeTarget == null || this.form.consumeTarget === '') {
        this.$message.warning('请填写门店消耗目标')
        return false
      }
      if (this.form.lifeline == null || this.form.lifeline === '') {
        this.$message.warning('请填写门店生命线')
        return false
      }
      if (this.form.headcountTarget == null || this.form.headcountTarget === '') {
        this.$message.warning('请填写门店人员目标')
        return false
      }
      if (this.form.assistPhase1 == null || this.form.assistPhase1 === '') {
        this.$message.warning('请填写店助人头目标（阶段一）')
        return false
      }
      if (this.form.assistPhase2 == null || this.form.assistPhase2 === '') {
        this.$message.warning('请填写店助人头目标（阶段二）')
        return false
      }
      return true
    },
    async handleSave() {
      if (!this.currentStoreId || !this.viewMonth) {
        this.$message.warning('门店信息异常')
        return
      }
      if (!this.validateForm()) return

      this.saving = true
      try {
        const payload = this.buildPayload()
        if (this.savedTarget && this.savedTarget.id) {
          await updateLqMdTarget(this.savedTarget.id, payload)
        } else {
          await createLqMdTarget(payload)
        }
        this.$message.success('目标设定成功')
        this.$emit('saved')
        await this.loadMonthData()
      } catch (e) {
        // 错误已由拦截器处理
      } finally {
        this.saving = false
      }
    },
    async handleCopyLastMonth() {
      if (!this.lastMonth) {
        this.$message.warning('无法获取上月信息')
        return
      }

      try {
        await this.loadDepartmentOptions()
        const res = await getLqMdTargetList({
          storeId: this.currentStoreId,
          month: this.lastMonth,
          currentPage: 1,
          pageSize: 1
        })

        const list = res.data?.list || []
        if (list.length > 0 && this.isTargetConfigured(list[0])) {
          this.form = this.mapTargetToForm(list[0])
          await this.ensureMissingDeptOptions()
          this.$message.success('已复制上月目标')
        } else {
          this.$message.warning('上月没有设定目标，无法复制')
        }
      } catch (e) {
        this.$message.warning('获取上月目标失败')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .target-dialog {
  max-width: 820px;
  margin-top: 5vh !important;
  border-radius: 20px;
  padding: 0;
  background: radial-gradient(
    circle at 0 0,
    rgba(255, 255, 255, 0.96) 0,
    rgba(248, 250, 252, 0.98) 40%,
    rgba(241, 245, 249, 0.98) 100%
  );
  box-shadow:
    0 24px 48px rgba(15, 23, 42, 0.18),
    0 0 0 1px rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);
  .el-dialog__header { display: none; }
  .el-dialog__body { padding: 0; }
}

.dialog-inner {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  padding: 18px 22px 16px;
}

.dialog-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 14px;
  background: rgba(219, 234, 254, 0.96);
}

.dialog-title-wrap {
  flex: 1;
  min-width: 0;
}

.dialog-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 17px;
  font-weight: 600;
  color: #0f172a;
}

.store-tag {
  font-size: 12px;
  font-weight: 500;
  color: #2563eb;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(37, 99, 235, 0.15);
  padding: 1px 8px;
  border-radius: 999px;
}

.dialog-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.dialog-close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  cursor: pointer;
  color: #64748b;
  margin-left: 10px;
  transition: all 0.15s;
  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #0f172a;
  }
}

.toolbar-row {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.month-picker {
  width: 168px;
}

.mode-badge {
  font-size: 11px;
  padding: 2px 10px;
  border-radius: 999px;
  line-height: 1.6;
  &--edit {
    background: #dcfce7;
    color: #166534;
  }
  &--readonly {
    background: #f1f5f9;
    color: #64748b;
  }
}

.summary-card {
  flex-shrink: 0;
  background: #fff;
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 12px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.6);
}

.summary-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.summary-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;
  i {
    color: #6366f1;
    font-size: 15px;
  }
}

.summary-amount {
  font-size: 20px;
  font-weight: 700;
  color: #6366f1;
  white-space: nowrap;
}

.summary-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #94a3b8;
}

.summary-warn {
  margin-left: 6px;
  color: #f56c6c;
  font-weight: 500;
}

.dialog-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
}

.section-panel {
  background: #fff;
  border-radius: 16px;
  padding: 14px 14px 6px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.05);
  border: 1px solid rgba(226, 232, 240, 0.6);
  &--mt {
    margin-top: 12px;
  }
}

.section-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
  i {
    color: #6366f1;
    font-size: 15px;
  }
}

.dept-row {
  padding: 10px 0;
  border-bottom: 1px dashed #e2e8f0;
  &:last-child {
    border-bottom: none;
    padding-bottom: 4px;
  }
}

.dept-row-label {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 6px;
}

.dept-tag {
  font-weight: 400;
  color: #64748b;
}

.dept-row-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 16px;
  align-items: start;
}

.dept-form-item {
  margin-bottom: 8px !important;
  &--target {
    min-width: 0;
  }
}

.extra-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 12px;
}

.extra-span-2 {
  grid-column: 1 / -1;
}

.full-width {
  width: 100%;
}

.dialog-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(229, 231, 235, 0.8);
}

::v-deep .target-dialog {
  .el-form-item__label {
    white-space: nowrap;
    color: #475569;
    font-size: 12px;
    line-height: 32px;
  }
  .el-form-item {
    margin-bottom: 10px;
  }
  .el-input__inner,
  .el-input-number .el-input__inner {
    border-radius: 999px;
    height: 32px;
    line-height: 32px;
    border-color: #e5e7eb;
    background-color: #f9fafb;
    text-align: left;
    padding-left: 14px;
    padding-right: 14px;
    &:focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.18);
    }
  }
  .el-select .el-input__inner {
    text-align: left;
  }
  .el-input-number.is-controls-right .el-input__inner {
    padding-left: 14px;
    padding-right: 14px;
  }
  .month-picker .el-input__inner {
    border-radius: 999px;
    height: 32px;
    line-height: 32px;
    border-color: #e5e7eb;
    background-color: #f9fafb;
    text-align: left;
    padding-left: 30px;
  }
  .el-button {
    margin-left: 0 !important;
  }
  .pill-btn.el-button--primary {
    border-radius: 999px;
    padding: 0 20px;
    height: 32px;
    line-height: 32px;
    background: #2563eb;
    border-color: #2563eb;
    box-shadow: 0 4px 10px rgba(37, 99, 235, 0.35);
    font-size: 13px;
    font-weight: 600;
  }
  .pill-btn--plain.el-button--default {
    border-radius: 999px;
    padding: 0 18px;
    height: 32px;
    line-height: 32px;
    background: rgba(239, 246, 255, 0.9);
    color: #2563eb;
    border-color: rgba(37, 99, 235, 0.18);
    font-size: 13px;
    &:hover {
      background: rgba(219, 234, 254, 0.95);
      color: #1d4ed8;
    }
  }
  .pill-btn--ghost.el-button--default {
    border-radius: 999px;
    padding: 0 16px;
    height: 32px;
    line-height: 32px;
    background: transparent;
    color: #64748b;
    border-color: #e2e8f0;
    font-size: 13px;
    &:hover {
      color: #2563eb;
      border-color: rgba(37, 99, 235, 0.25);
      background: rgba(239, 246, 255, 0.6);
    }
  }
}

@media (max-width: 680px) {
  .dept-row-fields {
    grid-template-columns: 1fr;
  }
  .extra-grid {
    grid-template-columns: 1fr;
  }
  .extra-span-2 {
    grid-column: auto;
  }
  .summary-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}
</style>
